import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { getAllCampaigns, getCalendars } from '../api';
import { countWarmUpsPendingReview, selectEditableWarmUps } from '../core/utils/warm-up';
import { useAccount } from '../store/account/hooks';
import {
  formValidation as accountValidation,
  isProfileScoreGateSatisfied,
  profileFormValidation,
} from '../components/account-info/utils';
import { ONBOARDING_STEPS } from '../core/constants';
import { useDomains } from '../store/domains/hooks';
import { usePaymentOptions } from '../store/payment-options/hooks';
import { useImports } from '../store/imports/hooks';
import { useTags } from '../store/tags/hook';
import { useSegments } from '../store/segments/hook';
import { usePages } from '../store/pages/hooks';

const isAccountInfoComplete = (account) => {
  try {
    accountValidation.validateSync(account);
    return true;
  } catch (err) {
    return false;
  }
};

const useOnboarding = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [warmUpsPendingReview, setWarmUpsPendingReview] = useState(0);
  const { account, getAccount } = useAccount();
  const { authenticatedDomain, getDomains } = useDomains();
  const { isStripeAccountConnected, isStripeConnectionComplete, getStripeAccount } = usePaymentOptions();
  const { imports, onboarding: importOnboarding, getImports } = useImports();
  const { getTags } = useTags();
  const { getSegments } = useSegments();
  const { getPages, pageList } = usePages();
  const ldClient = useLDClient();

  const getUserAccountData = useCallback(async () => {
    try {
      // .unwrap() will return a new Promise that either has the actual action.payload value
      // from a fulfilled action, or throws an error if it's the rejected action.
      // https://redux.js.org/tutorials/essentials/part-5-async-logic#checking-thunk-results-in-components
      const tenantAccount = await getAccount().unwrap();

      // DEBT: This should really be done in something like an auth provider
      ldClient.identify({
        key: tenantAccount.tenantId,
      });

      if (tenantAccount?.subscriptionStatus === 'pending') {
        setLoading(false);
        return;
      }

      if (!tenantAccount?.onboarded) {
        // If any of this actions fail, error will be handled by redux
        const [, , , , calendarsData, campaigns] = await Promise.all([
          getStripeAccount(),
          getDomains(),
          getPages(),
          getImports(),
          getCalendars().catch(() => []),
          // Same failure policy as WelcomeSeriesStep: a campaigns-api error counts as "no warm-ups"
          // so an outage can't strand a customer on the Welcome Series step.
          getAllCampaigns().catch((err) => {
            Sentry.captureException(err);
            return [];
          }),
        ]);
        setCalendars(calendarsData || []);
        setWarmUpsPendingReview(countWarmUpsPendingReview(selectEditableWarmUps(campaigns || [])));
      } else {
        // User has been onboarded, pre-fetch all needed data
        getTags();
        getSegments();
        getPages();
      }
    } catch (err) {
      // Get current account error
      Sentry.captureException(err);
      setError(true);
    }
    setLoading(false);
  }, [getAccount, getStripeAccount, getDomains, getImports, getTags, getSegments, getPages, ldClient]);

  useEffect(() => {
    getUserAccountData();
  }, [getUserAccountData]);

  const currentOnboardingStep = useMemo(() => {
    if (account?.onboarded || account?.sandbox) {
      // Onboarding has been successfully completed
      return ONBOARDING_STEPS.completed;
    }

    if (account?.subscriptionStatus === 'pending') {
      // User is pending, show the subscription step
      return ONBOARDING_STEPS.subscription;
    }

    // Welcome step links into account info step
    if (!isAccountInfoComplete(account)) {
      return ONBOARDING_STEPS.welcome;
    }

    // Stay on the organization profile step until it is filled out, valid, and
    // every scored question has been graded "Strong" (mirrors the in-form gate
    // in OrganizationProfileForm). Sandbox accounts are already short-circuited
    // to `completed` above, so the score gate always applies here unless it was
    // waived for this customer via `bypassProfileScoreRequirement`.
    if (
      !account?.profile ||
      !profileFormValidation.isValidSync(account?.profile) ||
      !isProfileScoreGateSatisfied(account?.profile, account?.profileFeedback, {
        scoreRequired: !account?.bypassProfileScoreRequirement,
      })
    ) {
      return ONBOARDING_STEPS.organizationProfile;
    }

    if (!isStripeAccountConnected || !isStripeConnectionComplete) {
      return ONBOARDING_STEPS.stripeAccount;
    }

    if (!pageList?.length || !account?.donationPageSubdomain) {
      return ONBOARDING_STEPS.donationPage;
    }

    if (!authenticatedDomain) {
      return ONBOARDING_STEPS.domainAuthentication;
    }

    if (!imports.length || importOnboarding) {
      return ONBOARDING_STEPS.importContacts;
    }

    if (!account.emailPreferences?.senderName || !account.emailPreferences?.senderEmail) {
      return ONBOARDING_STEPS.emailSettings;
    }

    // Warm-ups found here must be reviewed (saved once) before the step is done, mirroring its Next gate.
    if (!account.welcomeSeriesLaunched || warmUpsPendingReview > 0) {
      return ONBOARDING_STEPS.welcomeSeries;
    }

    const hasApprovedCalendar = calendars.some((calendar) => calendar.status === 'approved');
    if (!hasApprovedCalendar) {
      return ONBOARDING_STEPS.contentCalendar;
    }

    if (!account.onboardingCall) {
      return ONBOARDING_STEPS.scheduleCall;
    }

    return ONBOARDING_STEPS.congrats;
  }, [
    account,
    isStripeAccountConnected,
    isStripeConnectionComplete,
    authenticatedDomain,
    pageList,
    imports,
    importOnboarding,
    calendars,
    warmUpsPendingReview,
  ]);

  return { currentOnboardingStep, loading, error };
};

export default useOnboarding;
